import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { uploadLogoApiV1OrganizationsOrgIdLogoUploadPost } from '@/common/api/client/sdk.gen'
import type { OrganizationResponse, OrganizationWithUserRoleResponse } from '@/common/api/client/types.gen'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'

const uploadOrgLogo = (orgId: string, imageFile: File) =>
    uploadLogoApiV1OrganizationsOrgIdLogoUploadPost({
        path: { org_id: orgId },
        body: { image_file: imageFile },
    }).then(res => res.data!)

export const ORG_LOGO_MODAL_TOGGLE_ID = 'orgLogoUploadModal'

export const setOrgLogoModalOpen = (open: boolean) => {
    const el = document.getElementById(ORG_LOGO_MODAL_TOGGLE_ID) as HTMLInputElement | null
    if (el) {
        el.checked = open
    }
}

export const useOrgLogoUpload = (orgId: string) => {
    const queryClient = useQueryClient()
    const toast = useOptionalToast()
    const [seedFile, setSeedFile] = useState<File | null>(null)

    useEffect(() => {
        const el = document.getElementById(ORG_LOGO_MODAL_TOGGLE_ID) as HTMLInputElement | null
        if (!el) {
            return
        }
        const handleChange = () => {
            if (!el.checked) {
                setSeedFile(null)
            }
        }
        el.addEventListener('change', handleChange)
        return () => el.removeEventListener('change', handleChange)
    }, [])

    const uploadMutation = useMutation({
        mutationFn: (imageFile: File) => uploadOrgLogo(orgId, imageFile),
        onSuccess: data => {
            const logoUrl = data.image_url
            queryClient.setQueryData(
                ['current_org', orgId],
                (prev: OrganizationResponse | OrganizationWithUserRoleResponse | undefined) => {
                    if (!prev || prev.id !== orgId) {
                        return prev
                    }
                    return { ...prev, logo_url: logoUrl }
                }
            )
            queryClient.setQueryData(['my_orgs'], (old: OrganizationWithUserRoleResponse[] | undefined) =>
                (old ?? []).map(org => (org.id === orgId ? { ...org, logo_url: logoUrl } : org))
            )
            queryClient.invalidateQueries({ queryKey: ['orgs', orgId] })
            toast?.showToast({ type: 'success', message: 'Логотип обновлён' })
            setOrgLogoModalOpen(false)
        },
        onError: () => {
            toast?.showToast({ type: 'error', message: 'Не удалось загрузить логотип' })
        },
    })

    const onSeedFileConsumed = useCallback(() => {
        setSeedFile(null)
    }, [])

    const handleExport = useCallback(
        (file: File) => {
            return uploadMutation.mutateAsync(file)
        },
        [uploadMutation]
    )

    return {
        seedFile,
        onSeedFileConsumed,
        isUploadPending: uploadMutation.isPending,
        handleExport,
    }
}
